<template>
    <section class="section-card">
        <div class="section-card-header d-flex justify-content-between align-items-center">
            <h4 class="mb-0">Bill</h4>
            <span v-if="existingBillId" class="badge bg-light-primary">Editing Bill #{{ billFormData.bill_number }}</span>
        </div>
        <div class="section-card-body">
            <div class="row">
                <div class="col-md-12 mb-2">
                    <AsyncSelect
                        placeholder="Select product"
                        v-model="billProductId"
                        :api-service="fetchProductList"
                        :reduce="name => name.id"
                        :format-label="option => option.text"
                        :additional-query="{ product_type: 'services' }"
                        label="text"
                    />
                </div>
            </div>

            <div class="mb-2">
                <BillItemList :items="billItems" @onDeleteItem="onDeleteBillItem"/>
            </div>

            <BillTotals :items="billItems" :force-full-payment="fullBillDuringCheckIn" :applied-advance="appliedAdvance"/>

            <div v-if="canAdjustAdvance" class="advance-box mb-2">
                <div class="d-flex justify-content-between align-items-center">
                    <label class="form-check-label fw-bold" for="adjustAdvance">
                        <input id="adjustAdvance" v-model="adjustAdvance" type="checkbox" class="form-check-input me-1"/>
                        Adjust Pre-booking Advance
                    </label>
                    <span class="badge bg-light-success">Available: {{ commaFormat(availableAdvance) }}</span>
                </div>
                <div v-for="receipt in props.advanceReceipts" :key="receipt.id" class="advance-row">
                    <span>{{ receipt.voucher_no }} <small class="text-muted">{{ receipt.receipt_date }}</small></span>
                    <span>{{ commaFormat(receipt.available_amount) }}</span>
                </div>
                <div class="advance-row advance-applied">
                    <span>Adjusted from Advance</span>
                    <span>{{ commaFormat(appliedAdvance) }}</span>
                </div>
                <div class="advance-row advance-applied">
                    <span>Cash Payable After Adjustment</span>
                    <span>{{ commaFormat(cashPayable) }}</span>
                </div>
            </div>

            <div v-if="existingBillId && availableAdvance > 0" class="alert alert-warning py-1 px-2 mb-2">
                Advance can only be adjusted while creating the bill.
            </div>

            <div class="row">
                <div class="col-12 mb-1">
                    <label class="form-label" for="billDescription">Ledger Memo</label>
                    <textarea
                        id="billDescription"
                        v-model="billFormData.description"
                        class="form-control"
                        rows="2"
                    ></textarea>
                </div>
                <div class="col-12 mt-auto mb-1">
                    <div class="d-flex flex-wrap gap-1 justify-content-end">
                        <button type="button" class="btn btn-primary" :disabled="savingBill" @click="saveBill(false)">Save</button>
                        <button type="button" class="btn btn-primary" :disabled="savingBill" @click="saveBill(true)">Save & Print</button>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup>
import AsyncSelect from '@/components/molecule/input-field/AsyncSelect.vue';
import BillItemList from '@/components/molecule/company/hotel/hotel-checkin/BillItemList.vue';
import BillTotals from '@/components/molecule/company/hotel/hotel-checkin/BillTotals.vue';
import { computed, inject, onMounted, provide, reactive, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import handlePurchase from '@/services/modules/purchase';
import handleInventory from '@/services/modules/inventory';
import handleCompany from '@/services/modules/company';
import handleHotel from '@/services/modules/hotel/hotel';
import hospitalPdfPrinterHelper from '@/services/utils/hospitalPdfPrinterHelper';
import { getPreferenceValue, parsePreferenceBoolean } from '@/services/utils/global';
import figureFormatter from '@/services/utils/figureFormatter';

const props = defineProps({
    ipdRegisterId: {
        type: [Number, String],
        required: true,
    },
    guestId: {
        type: [Number, String],
        default: null,
    },
    rooms: {
        type: Array,
        default: () => [],
    },
    nights: {
        type: Number,
        default: 1,
    },
    advanceReceipts: {
        type: Array,
        default: () => [],
    },
});

const emit = defineEmits(['onBillSaved']);

const $route = useRoute();
const showError = inject('showError');
const showSuccess = inject('showSuccess');
const {
    fetchProductList,
    fetchAccountReceivable,
    fetchVatPayable,
    getAccountHeadBySlag,
    storeBillPayment,
    fetchLatestSaleByIpd,
    fetchSalesBill,
    updateSalesBill,
} = handlePurchase();
const { getProduct } = handleInventory();
const { fetchCompanyInfo, getCompanyPreferences } = handleCompany();
const { fetchHotelRoomProducts } = handleHotel();
const { generatePdf } = hospitalPdfPrinterHelper();
const { commaFormat } = figureFormatter();

const savingBill = ref(false);
const billLoaded = ref(false);
const fullBillDuringCheckIn = ref(false);
const existingBillId = ref(null);
const billProductId = ref(null);
const billItems = reactive([]);
const company = ref({});
const salesAccountHeads = ref([]);
const discountAccountHeads = ref([]);
const billFormData = ref({
    company_id: $route.params.companyId,
    contact_profile_id: props.guestId,
    account_head_id: null,
    ipd_register_id: props.ipdRegisterId,
    vat_payable_account_head_id: null,
    mop_account_head_id: null,
    receipt_reference: null,
    receipt_description: 'Hotel bill payment',
    payment: 0,
    discount_head_id: null,
    adjustment_amount: 0,
    service_charge_head_id: null,
    service_charge_amount: 0,
    status: 'active',
    bill_number: '',
    date: '',
    has_item_detail: true,
    description: '',
    item_details: [],
});

provide('formData', billFormData);

const adjustAdvance = ref(true);

const canAdjustAdvance = computed(() => {
    if (existingBillId.value) return false;
    return props.advanceReceipts.length > 0;
});

const availableAdvance = computed(() => {
    return props.advanceReceipts.reduce((sum, receipt) => {
        return sum + Number(receipt.available_amount);
    }, 0);
});

const billGrandTotal = computed(() => {
    const itemTotal = billItems.reduce((sum, item) => {
        return sum + (Number(item.quantity) * Number(item.rate));
    }, 0);
    return itemTotal - Number(billFormData.value.adjustment_amount || 0) + Number(billFormData.value.service_charge_amount || 0);
});

const appliedAdvance = computed(() => {
    if (! canAdjustAdvance.value) return 0;
    if (! adjustAdvance.value) return 0;
    return Math.min(availableAdvance.value, billGrandTotal.value);
});

const cashPayable = computed(() => {
    return billGrandTotal.value - appliedAdvance.value;
});

watch(() => props.guestId, (guestId) => {
    billFormData.value.contact_profile_id = guestId;
});

const getBaseQuery = () => {
    return `?company_id=${$route.params.companyId}`;
};

const onDeleteBillItem = (index) => {
    billItems.splice(index, 1);
};

watch(billProductId, async () => {
    if (! billProductId.value) return;
    const found = billItems.find(item => item.product_id === billProductId.value && ! item.service_resource_id);
    if (found) {
        showError('Item already selected!');
        billProductId.value = null;
        return;
    }
    const res = await getProduct({ id: billProductId.value, company_id: $route.params.companyId });
    billProductId.value = null;
    if (! res.data) {
        showError('Something went wrong please try again');
        return;
    }
    const product = res.data;
    billItems.push({
        product_id: product.id,
        name: product.name,
        description: product.description.unit.description,
        quantity: 1,
        rate: product.description.sales_price,
        discount: 0,
        vat: product.description.vat_rate,
        sub_total: 0,
        discount_amount: 0,
        discount_percent: 0,
        vat_amount: 0,
        total: 0,
        is_edit: false,
        product: { name: product.name },
    });
});

const buildRoomProductQuery = () => {
    const roomIds = props.rooms.map(room => room.service_resource_id).filter(id => id);
    const roomIdQuery = roomIds.map(id => `room_ids[]=${id}`).join('&');
    return `${getBaseQuery()}&${roomIdQuery}`;
};

const resolveRoomRate = (room, product) => {
    if (! product.is_room_charge) return Number(product.rate);
    const roomRate = Number(room.rate);
    if (isNaN(roomRate)) return Number(product.rate);
    if (roomRate <= 0) return Number(product.rate);
    return roomRate;
};

const resolveQuantity = (product) => {
    if (! product.is_room_charge) return 1;
    return props.nights;
};

const buildRoomBillItem = (room, product) => {
    return {
        product_id: product.product_id,
        product_type: product.product_type,
        service_resource_description_id: product.service_resource_description_id,
        service_resource_id: room.service_resource_id,
        is_room_charge: product.is_room_charge,
        name: `${product.name} - ${room.name}`,
        description: product.description,
        quantity: resolveQuantity(product),
        rate: resolveRoomRate(room, product),
        default_rate: Number(product.rate),
        discount: 0,
        vat: product.vat,
        sub_total: 0,
        discount_amount: 0,
        discount_percent: 0,
        vat_amount: 0,
        total: 0,
        is_edit: false,
        product: { name: `${product.name} - ${room.name}` },
    };
};

const detachRoomItem = (item) => {
    item.service_resource_id = null;
    item.service_resource_description_id = null;
    item.is_room_charge = false;
};

const dropUnselectedRoomItems = () => {
    const roomIds = props.rooms.map(room => String(room.service_resource_id));
    for (let index = billItems.length - 1; index >= 0; index--) {
        const item = billItems[index];
        if (! item.service_resource_id) continue;
        if (roomIds.includes(String(item.service_resource_id))) continue;
        if (item.is_edit) {
            detachRoomItem(item);
            continue;
        }
        billItems.splice(index, 1);
    }
};

const applyRoomProducts = (roomProducts) => {
    dropUnselectedRoomItems();
    props.rooms.forEach(room => {
        const products = roomProducts[room.service_resource_id];
        if (! products) return;
        products.forEach(product => {
            const found = billItems.find(item => item.service_resource_description_id === product.service_resource_description_id);
            if (found) return;
            billItems.push(buildRoomBillItem(room, product));
        });
    });
    syncRoomChargeRates();
};

const syncRoomChargeRates = () => {
    props.rooms.forEach(room => {
        const item = billItems.find(billItem => billItem.service_resource_id === room.service_resource_id && billItem.is_room_charge);
        if (! item) return;
        if (item.is_edit) return;
        item.quantity = props.nights;
        item.rate = resolveRoomRate(room, { is_room_charge: true, rate: item.default_rate });
    });
};

const loadRoomProducts = async () => {
    if (! billLoaded.value) return;
    if (existingBillId.value) return;
    if (! props.rooms.length) {
        dropUnselectedRoomItems();
        return;
    }
    await fetchHotelRoomProducts(buildRoomProductQuery()).then(res => {
        if (! res.status) return;
        applyRoomProducts(res.data);
    });
};

const loadBillAccountHeads = async () => {
    const companyQuery = getBaseQuery();
    await Promise.all([
        fetchAccountReceivable(companyQuery).then(res => {
            if (res.data) billFormData.value.account_head_id = res.data[0].id;
        }),
        fetchVatPayable(companyQuery).then(res => {
            if (res.data) billFormData.value.vat_payable_account_head_id = res.data[0].id;
        }),
        getAccountHeadBySlag('discount_account', companyQuery).then(res => {
            if (res.data) discountAccountHeads.value = res.data;
        }),
        getAccountHeadBySlag('sales_accounts', companyQuery).then(res => {
            if (res.data) salesAccountHeads.value = res.data;
        }),
    ]);
};

const applyBillData = (bill) => {
    billFormData.value.company_id = bill.company_id;
    billFormData.value.contact_profile_id = bill.contact_profile_id;
    billFormData.value.account_head_id = bill.account_head_id;
    billFormData.value.ipd_register_id = bill.ipd_register_id;
    billFormData.value.vat_payable_account_head_id = bill.vat_payable_account_head_id;
    billFormData.value.discount_head_id = bill.discount_head_id;
    billFormData.value.adjustment_amount = bill.round_up_discount;
    billFormData.value.service_charge_head_id = bill.service_charge_head_id;
    billFormData.value.service_charge_amount = bill.service_charge_amount;
    billFormData.value.status = 'active';
    billFormData.value.bill_number = bill.bill_number;
    billFormData.value.date = bill.date;
    billFormData.value.has_item_detail = bill.has_item_detail;
    billFormData.value.description = bill.description;
    billFormData.value.item_details = bill.general;

    billItems.splice(0, billItems.length);
    bill.general.forEach(({ product, ...item }) => {
        billItems.push({ ...item, name: product.name, product: { name: product.name } });
    });

    const receiptGeneral = bill.receipt_generals && bill.receipt_generals[0];
    if (! receiptGeneral) return;
    billFormData.value.mop_account_head_id = receiptGeneral.receipt_master.account_head_id;
    billFormData.value.receipt_master_id = receiptGeneral.receipt_master.id;
    billFormData.value.receipt_reference = receiptGeneral.receipt_master.receipt_reference;
    billFormData.value.payment = Number(receiptGeneral.receipt_master.total_paid_amount || 0);
};

const loadBill = async () => {
    await fetchLatestSaleByIpd(props.ipdRegisterId, getBaseQuery()).then(async (res) => {
        if (! res.status) return;
        existingBillId.value = res.data.id;
        await fetchSalesBill(res.data.id, getBaseQuery()).then(billRes => {
            if (billRes.data) applyBillData(billRes.data);
        });
    }).finally(() => {
        billLoaded.value = true;
    });
};

const validateBill = () => {
    if (! billItems.length) {
        showError('Please add at least one item to the bill');
        return false;
    }
    return true;
};

const applyAdvanceToPayload = (payload) => {
    const adjustAmount = appliedAdvance.value;
    if (adjustAmount <= 0) return;
    payload.advance_amount = adjustAmount;
    payload.advance_receipts = buildAdvanceReceipts(adjustAmount);
    const cashPayable = billGrandTotal.value - adjustAmount;
    payload.payment = Math.max(0, Math.min(Number(payload.payment || 0), cashPayable));
};

const buildAdvanceReceipts = (adjustAmount) => {
    let remaining = adjustAmount;
    const allocations = [];
    props.advanceReceipts.forEach(receipt => {
        if (remaining <= 0) return;
        const usable = Math.min(Number(receipt.available_amount), remaining);
        if (usable <= 0) return;
        allocations.push({ advance_receipt_id: receipt.id, advance_amount: Number(usable.toFixed(2)) });
        remaining = Number((remaining - usable).toFixed(2));
    });
    return allocations;
};

const submitBill = (payload) => {
    if (existingBillId.value) return updateSalesBill(existingBillId.value, payload);
    return storeBillPayment(payload);
};

const goToPrint = (id) => {
    fetchSalesBill(id, getBaseQuery()).then((res) => {
        if (! res.data) return;
        generatePdf(company.value, res.data, 'Hotel Bill', false, true);
    });
};

const saveBill = async (print) => {
    if (! validateBill()) return;
    billFormData.value.date = billFormData.value.date ? billFormData.value.date : new Date().toISOString().split('T')[0];
    billFormData.value.item_details = billItems;
    const payload = JSON.parse(JSON.stringify(billFormData.value));
    payload.item_details = billFormData.value.item_details;
    payload.sale_type = 'ipd_invoice';
    payload.receipt_type = 'ipd_invoice';
    applyAdvanceToPayload(payload);

    savingBill.value = true;
    await submitBill(payload).then(async (res) => {
        if (! res.status) {
            showError(res.message);
            return;
        }
        showSuccess(res.message);
        if (print === true) goToPrint(res.data.id);
        emit('onBillSaved');
        await loadBill();
    }).finally(() => {
        savingBill.value = false;
    });
};

const loadCompany = async () => {
    await fetchCompanyInfo($route.params.companyId).then(res => {
        if (res.data) company.value = res.data;
    });
};

const loadBillPreference = async () => {
    const queryObject = {
        company_id: $route.params.companyId,
        fields: 'full_hotel_bill_during_check_in',
    };
    const query = '?' + new URLSearchParams(queryObject).toString();

    await getCompanyPreferences(query).then(res => {
        if (! res.status) return;
        if (! res.data) return;
        fullBillDuringCheckIn.value = parsePreferenceBoolean(getPreferenceValue(res.data, 'full_hotel_bill_during_check_in'));
    }).catch(() => {
        fullBillDuringCheckIn.value = false;
    });
};

watch(() => props.rooms.map(room => room.service_resource_id).join(','), async () => {
    await loadRoomProducts();
});

watch(() => props.rooms.map(room => `${room.service_resource_id}:${room.rate}`).join(','), () => {
    syncRoomChargeRates();
});

watch(() => props.nights, () => {
    syncRoomChargeRates();
});

onMounted(async () => {
    await loadBillPreference();
    await Promise.all([
        loadBillAccountHeads(),
        loadBill(),
        loadCompany(),
    ]);
    await loadRoomProducts();
});
</script>

<style scoped>
.advance-box {
    border: 1px solid #cdeed8;
    background: #f4fbf6;
    border-radius: 0.5rem;
    padding: 0.6rem 0.75rem;
    font-size: 0.85rem;
}
.advance-row {
    display: flex;
    justify-content: space-between;
    padding-top: 0.35rem;
}
.advance-applied {
    font-weight: 700;
    border-top: 1px solid #cdeed8;
    margin-top: 0.35rem;
}
</style>
